import classNames from "classnames"
import styles from "./styles.module.scss"
import AlertCircleOutlineIcon from "~icons/eva/alert-circle-outline"

const mask = (value: string, pattern: string): string => {
  const digits = value.replace(/[^\d*•]/g, "")

  if (!digits || !pattern) return digits

  let result = ""
  let digitIndex = 0

  for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
    if (pattern[i] === "#") {
      result += digits[digitIndex]
      digitIndex++
    } else {
      result += pattern[i]
    }
  }

  return result
}

export const DefaultInput = ({
  innerRef,
  type,
  value,
  onChange,
  formatText,
  onKeyDown,
  name,
  label,
  parentBackgroundColor,
  lightOutline = false,
  errorMessage,
  iconLeft,
  iconRight,
  onFocus,
  onBlur,
  disabled,
  testId,
  onClick,
  className,
  readOnly,
  resize = false,
  rows,
  handlePhoneValueChange,
  isModalOpen,
  inputClassName,
  maxLength,
  inputColor,
  maskValue,
  inputMode = "text",
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  innerRef?: any
  type?: string
  value: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onChange: (value: string) => void
  formatText?: (value: string) => string
  name: string
  label: string
  parentBackgroundColor: string
  lightOutline?: boolean
  errorMessage?: string
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  testId?: string
  onClick?: () => void
  className?: string
  readOnly?: boolean
  resize?: boolean
  rows?: number
  inputClassName?: string
  handlePhoneValueChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => string
  isModalOpen?: boolean
  maxLength?: number
  inputColor?: string
  maskValue?: string
  inputMode?: "text" | "numeric"
}) => {
  const inputStyle = inputColor
    ? {
        color: inputColor,
      }
    : {}

  const maskedValue = maskValue ? (value ? mask(value, maskValue) : value) : value

  return (
    <div className={classNames(styles.inputContainer, className ? className : "")}>
      {iconLeft && <div className={styles.iconLeft}>{iconLeft}</div>}
      {type === "textarea" ? (
        <textarea
          id={name}
          name={name}
          ref={innerRef}
          value={value}
          onChange={(e) => {
            let cleanText = e.target.value
            if (formatText) {
              cleanText = formatText(cleanText)
            }
            onChange(cleanText)
          }}
          className={classNames(
            iconRight ? styles.inputWithIcon : "",
            lightOutline ? styles.lightOutline : styles.darkOutline,
          )}
          disabled={disabled}
          data-testid={testId}
          onClick={onClick}
          readOnly={readOnly}
          rows={rows}
          autoComplete="off"
          inputMode={inputMode}
          style={{ resize: resize ? "vertical" : "none" }}
        />
      ) : (
        <input
          id={name}
          name={name}
          ref={innerRef}
          type={type || "text"}
          value={maskedValue}
          style={inputStyle}
          autoComplete="off"
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          onChange={(e) => {
            if (handlePhoneValueChange) {
              handlePhoneValueChange(e)
            } else {
              let cleanText = e.target.value
              if (formatText) {
                cleanText = formatText(cleanText)
              } else if (maskValue) {
                cleanText = mask(cleanText, maskValue)
              }
              onChange(cleanText)
            }
          }}
          className={classNames(
            iconRight ? styles.inputWithIconRight : "",
            iconLeft ? styles.inputWithIconLeft : "",
            lightOutline ? styles.lightOutline : styles.darkOutline,
            inputClassName ? inputClassName : "",
          )}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          data-testid={testId}
          onClick={onClick}
          readOnly={readOnly}
          inputMode={inputMode}
        />
      )}
      <label
        className={value || isModalOpen ? styles.filled : ""}
        htmlFor={name}
        style={{
          backgroundImage:
            disabled && !value
              ? "none"
              : disabled
                ? `linear-gradient(to top, ${parentBackgroundColor} 6px, transparent 5px)`
                : `linear-gradient(to top, ${parentBackgroundColor} 9px, transparent 8px)`,
          backgroundColor: "transparent",
        }}
      >
        {label}
      </label>
      {iconRight && <div className={styles.iconRight}>{iconRight}</div>}
      {errorMessage && errorMessage !== "-" && (
        <div className={styles.errorContainer}>
          <AlertCircleOutlineIcon className={styles.errorIcon} />
          <p>{errorMessage}</p>
        </div>
      )}
    </div>
  )
}
